// Step 4 — floating-action safe-area hook.
// Detects when a text input is focused or the soft keyboard is open so
// floating buttons (camera FAB) can hide instead of covering DM composers,
// search bars, or comment sheets. Also exposes the canonical bottom offset
// that keeps FABs clear of the BottomNav + iOS home indicator.
import { useEffect, useState } from 'react';

const isEditable = (el) => {
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const t = (el.type || 'text').toLowerCase();
    return !['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'color'].includes(t);
  }
  return el.isContentEditable === true;
};

// Bottom offset: 64px BottomNav + breathing room + device safe-area.
export const FAB_BOTTOM_OFFSET = 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)';

export const useFloatingActionSafeArea = () => {
  const [inputFocused, setInputFocused] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const onFocusIn = (e) => { if (isEditable(e.target)) setInputFocused(true); };
    const onFocusOut = () => {
      // Re-check after the browser settles focus (tab between inputs).
      setTimeout(() => setInputFocused(isEditable(document.activeElement)), 80);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => setKeyboardOpen(window.innerHeight - vv.height > 140);
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  // Hide while scrolling down (content browsing), reveal on scroll up or
  // near the top — keeps the FAB clear of feed action rails mid-scroll.
  const [scrollHidden, setScrollHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        if (y < 80) setScrollHidden(false);
        else if (delta > 12) setScrollHidden(true);
        else if (delta < -12) setScrollHidden(false);
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return {
    suppressed: inputFocused || keyboardOpen || scrollHidden,
    inputFocused,
    keyboardOpen,
    scrollHidden,
    bottomOffset: FAB_BOTTOM_OFFSET,
  };
};

export default useFloatingActionSafeArea;
